import { appConfig } from "@/types/appConfig.type.ts";

type ZendeskChannel = "messenger" | "webWidget";
type ZendeskAction = "show" | "open" | "close" | "hide";

declare global {
  interface Window {
    zE?: (channel: ZendeskChannel, action: ZendeskAction) => void;
  }
}

export function useZendeskChat() {
  const hasZendeskApi = (): boolean => typeof window.zE === "function";

  const runZendeskAction = (
    channel: ZendeskChannel,
    action: ZendeskAction,
  ): boolean => {
    if (!hasZendeskApi()) return false;

    try {
      window.zE?.(channel, action);
      return true;
    } catch {
      return false;
    }
  };

  const mount = (): void => {
    if (!appConfig.VITE_ZENDESK_CHAT_URL) return;

    if (!document.getElementById("ze-snippet")) {
      const script = document.createElement("script");
      script.setAttribute("src", appConfig.VITE_ZENDESK_CHAT_URL);
      script.setAttribute("id", "ze-snippet");
      document.head.appendChild(script);
    }
  };

  const show = (): boolean => {
    const messengerShown = runZendeskAction("messenger", "show");
    const webWidgetShown = runZendeskAction("webWidget", "show");
    return messengerShown || webWidgetShown;
  };

  const open = (): boolean => {
    const messengerOpened = runZendeskAction("messenger", "open");

    if (messengerOpened) {
      return true;
    }

    const widgetShown = runZendeskAction("webWidget", "show");
    const widgetOpened = runZendeskAction("webWidget", "open");

    return widgetShown || widgetOpened;
  };

  const hide = (): void => {
    runZendeskAction("messenger", "close");
    runZendeskAction("messenger", "hide");
    runZendeskAction("webWidget", "close");
    runZendeskAction("webWidget", "hide");
  };

  const unmount = (): void => {
    hide();
  };

  return { mount, unmount, show, open, hide };
}
